import React from 'react';
import { bool, arrayOf, oneOfType } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IoIosArrowRoundBack } from 'react-icons/io';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isNotFoundError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { ARTIST_PROFILE_TABS } from './ArtistProfilePage.duck';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  IconSpinner,
  InlineTextButton,
  Page,
  LinkTabNavHorizontal,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import SearchResultsPanel from '../SearchPage/SearchResultsPanel/SearchResultsPanel';

import ArtistHeader from './ArtistHeader';
import css from './ArtistProfilePage.module.css';

export const ArtistProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();

  const {
    scrollingDisabled,
    params: pathParams,
    user,
    userShowError,
    isClientProfile,
    listings,
    queryListingsInProgress,
    queryListingsError,
    pagination,
    reviews,
    queryReviewsError,
    location,
  } = props;

  const { id, tab } = pathParams;

  if (!ARTIST_PROFILE_TABS.includes(tab)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const isDataLoaded = user != null || userShowError != null;
  if (!isDataLoaded) {
    return null;
  } else if (isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const { displayName, bio, publicData, metadata } = user?.attributes?.profile || {};
  const { userFields } = config.user;
  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ArtistProfilePage.schemaTitle' }, schemaTitleVars);

  const tabs = [
    {
      text: <FormattedMessage id="ArtistProfilePage.portfolioTab" />,
      selected: tab === 'portfolio',
      linkProps: { name: 'ArtistProfilePage', params: { id, tab: 'portfolio' } },
    },
    {
      text: <FormattedMessage id="ArtistProfilePage.flashTab" />,
      selected: tab === 'flash',
      linkProps: { name: 'ArtistProfilePage', params: { id, tab: 'flash' } },
    },
  ];

  const content = isClientProfile ? (
    <div className={css.notAnArtist}>
      <Heading as="h1" rootClassName={css.notAnArtistTitle}>
        <FormattedMessage id="ArtistProfilePage.notAnArtistTitle" />
      </Heading>
      <p className={css.notAnArtistDescription}>
        <FormattedMessage id="ArtistProfilePage.notAnArtistDescription" />
      </p>
    </div>
  ) : (
    <>
      <LinkTabNavHorizontal className={css.tabs} tabRootClassName={css.tab} tabs={tabs} />
      {queryListingsInProgress ? (
        <div className={css.loadingContainer}>
          <IconSpinner />
        </div>
      ) : queryListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="ArtistProfilePage.loadingDataFailed" />
        </p>
      ) : (
        <SearchResultsPanel
          className={css.searchResultsPanel}
          listings={listings}
          pagination={pagination}
          search={parse(location.search)}
          pageName="ArtistProfilePage"
          pagePathParams={{ id, tab }}
          showAuthorInfo={false}
          isMapVariant={false}
        />
      )}
    </>
  );

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.contentContainer}>
          <InlineTextButton
            type="button"
            className={css.backButton}
            onClick={() => history.goBack()}
          >
            <IoIosArrowRoundBack size={24} />
            <FormattedMessage id="back" />
          </InlineTextButton>
          <ArtistHeader
            user={user}
            displayName={displayName}
            bio={bio}
            publicData={publicData}
            metadata={metadata}
            userFieldConfig={userFields}
            reviews={reviews}
            intl={intl}
          />
          {content}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ArtistProfilePageComponent.defaultProps = {
  user: null,
  userShowError: null,
  isClientProfile: false,
  listings: [],
  queryListingsInProgress: false,
  queryListingsError: null,
  pagination: null,
  reviews: [],
  queryReviewsError: null,
};

ArtistProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  user: propTypes.user,
  userShowError: propTypes.error,
  isClientProfile: bool,
  listings: arrayOf(oneOfType([propTypes.listing, propTypes.ownListing])),
  queryListingsInProgress: bool,
  queryListingsError: propTypes.error,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,
};

const mapStateToProps = (state, ownProps) => {
  const tab = ownProps.params?.tab;
  const {
    userId,
    userShowError,
    isClientProfile,
    reviews,
    queryReviewsError,
    portfolio,
    flash,
  } = state.ArtistProfilePage;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  const activeTab = tab === 'flash' ? flash : portfolio;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    user,
    userShowError,
    isClientProfile,
    listings: getMarketplaceEntities(state, activeTab.listingRefs),
    queryListingsInProgress: activeTab.inProgress,
    queryListingsError: activeTab.error,
    pagination: activeTab.pagination,
    reviews,
    queryReviewsError,
  };
};

const ArtistProfilePage = compose(connect(mapStateToProps))(ArtistProfilePageComponent);

export default ArtistProfilePage;
